import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, ArrowRight, Shield, Ban, Users, AlertTriangle, Eye, Zap, Lock, TrendingUp } from 'lucide-react';
import SEO from '../SEO';
import '../SeoPages.css';

function TikTokGuidelinesGuide() {
  return (
    <>
      <SEO
        title="TikTok Community Guidelines: What Gets Your Videos Removed (2026)"
        description="Complete guide to TikTok's Community Guidelines. Learn what content is banned, what gets shadowbanned, and how to keep your TikTok account safe."
        keywords="TikTok community guidelines, TikTok rules, TikTok banned content, TikTok shadowban, TikTok content policy, what's not allowed on TikTok, TikTok account banned"
        url="/guides/tiktok-guidelines"
      />
      <div className="seo-page">
        <div className="seo-container">
          {/* Header */}
          <header className="seo-header">
            <Link to="/" className="seo-back-link">
              <ArrowLeft size={20} />
              <span>Back to Home</span>
            </Link>
            <Link to="/" className="seo-brand">
              <div className="seo-logo-circle">
                <Sparkles className="seo-sparkles-icon" />
              </div>
              <span className="seo-brand-name">MyCleanStudio</span>
            </Link>
          </header>

          {/* Hero */}
          <div className="seo-hero">
            <h1>TikTok Community Guidelines: What Gets Your Videos Removed</h1>
            <p className="seo-hero-subtitle">
              Understand TikTok's content rules to avoid shadowbans, video removals, and account termination.
            </p>
          </div>

          {/* Content */}
          <main className="seo-content">
            <div className="seo-toc">
              <h3>In This Guide</h3>
              <ul>
                <li><a href="#overview">TikTok Guidelines Overview</a></li>
                <li><a href="#prohibited">Prohibited Content</a></li>
                <li><a href="#restricted">Restricted Content</a></li>
                <li><a href="#shadowban">Understanding Shadowbans</a></li>
                <li><a href="#fyp">Getting on the For You Page</a></li>
                <li><a href="#stay-safe">How to Stay Safe</a></li>
              </ul>
            </div>

            <section id="overview">
              <h2>TikTok Guidelines Overview</h2>
              <p>
                TikTok's Community Guidelines define what content is allowed on the platform. TikTok is particularly strict because of its younger user base - content that might be fine elsewhere can get you banned on TikTok.
              </p>
              <p>
                Violating TikTok's guidelines can result in:
              </p>
              <ul>
                <li><strong>Video removal</strong> - Your video gets taken down</li>
                <li><strong>Shadowban</strong> - Reduced reach without notification</li>
                <li><strong>Account warning</strong> - Strike on your account</li>
                <li><strong>Temporary ban</strong> - Can't post for a period</li>
                <li><strong>Permanent ban</strong> - Account terminated</li>
              </ul>

              <div className="seo-warning-box">
                <p>
                  <strong>Warning:</strong> TikTok's enforcement can seem inconsistent. A video might go viral one day and get removed the next. This is why pre-checking content is essential.
                </p>
              </div>
            </section>

            <section id="prohibited">
              <h2>Prohibited Content</h2>
              <p>
                The following content is <strong>strictly prohibited</strong> and will result in immediate removal and potential account action:
              </p>

              <div className="seo-problem-grid">
                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <Ban size={20} />
                  </div>
                  <h4>Violent Content</h4>
                  <p>Graphic violence, gore, fights, abuse, or any content depicting harm to people or animals.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <Users size={20} />
                  </div>
                  <h4>Hate Speech</h4>
                  <p>Content attacking people based on race, ethnicity, religion, gender, sexual orientation, or disability.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <AlertTriangle size={20} />
                  </div>
                  <h4>Dangerous Activities</h4>
                  <p>Content showing or encouraging dangerous challenges, stunts, or activities that could cause harm.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <Eye size={20} />
                  </div>
                  <h4>Sexual Content</h4>
                  <p>Nudity, sexual acts, or sexually explicit content. TikTok is stricter than most platforms here.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon purple">
                    <Lock size={20} />
                  </div>
                  <h4>Minor Safety</h4>
                  <p>Any content that exploits, endangers, or sexualizes minors in any way.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon yellow">
                    <AlertTriangle size={20} />
                  </div>
                  <h4>Self-Harm Content</h4>
                  <p>Content depicting, promoting, or glorifying self-harm, suicide, or eating disorders.</p>
                </div>
              </div>

              <h3>Also Prohibited</h3>
              <ul>
                <li><strong>Illegal activities</strong> - Drug use, weapons, theft, fraud</li>
                <li><strong>Harassment</strong> - Bullying, threats, doxxing, coordinated attacks</li>
                <li><strong>Misinformation</strong> - Fake news, conspiracy theories, medical misinformation</li>
                <li><strong>Spam</strong> - Fake engagement, misleading content, scams</li>
                <li><strong>Impersonation</strong> - Pretending to be someone else</li>
                <li><strong>Intellectual property violations</strong> - Using others' content without permission</li>
              </ul>
            </section>

            <section id="restricted">
              <h2>Restricted Content</h2>
              <p>
                Some content isn't outright banned but will be restricted - meaning it won't appear on the For You Page and may only be visible to followers:
              </p>

              <h3>Content That Gets Restricted</h3>
              <ul>
                <li><strong>Suggestive content</strong> - Revealing clothing, suggestive dancing, innuendo</li>
                <li><strong>Mature themes</strong> - Content about dating, relationships, alcohol (even if legal)</li>
                <li><strong>Controversial topics</strong> - Politics, social issues, debates</li>
                <li><strong>Profanity</strong> - Even bleeped curse words can limit reach</li>
                <li><strong>Shocking content</strong> - Pranks, jumpscares, disturbing (but not violating) content</li>
                <li><strong>Gambling</strong> - Even references to betting or casinos</li>
              </ul>

              <div className="seo-highlight-box">
                <p>
                  <strong>Creator Fund Impact:</strong> If you're monetizing through the Creator Fund, restricted content won't earn money even if it gets views. TikTok's ad program has even stricter content requirements.
                </p>
              </div>
            </section>

            <section id="shadowban">
              <h2>Understanding Shadowbans</h2>
              <p>
                A "shadowban" is when TikTok reduces your content's visibility without telling you. Your videos might get zero views on the FYP while still being visible to followers.
              </p>

              <h3>Signs You're Shadowbanned</h3>
              <ul>
                <li>Sudden dramatic drop in views</li>
                <li>Videos not appearing in hashtag searches</li>
                <li>No views from "For You" traffic source</li>
                <li>New videos getting almost no engagement</li>
              </ul>

              <h3>Common Causes</h3>
              <ul>
                <li>Posting borderline content that doesn't quite violate guidelines</li>
                <li>Using banned hashtags or sounds</li>
                <li>Spammy behavior (mass following, repetitive content)</li>
                <li>Multiple community guideline violations</li>
                <li>Being reported by many users</li>
              </ul>

              <h3>How to Recover</h3>
              <ul>
                <li>Delete any borderline or flagged content</li>
                <li>Take a break from posting (2-7 days)</li>
                <li>Switch to completely safe, original content</li>
                <li>Avoid trending sounds that might be restricted</li>
                <li>Engage genuinely (don't spam comments)</li>
              </ul>
            </section>

            <section id="fyp">
              <h2>Getting on the For You Page</h2>
              <p>
                The For You Page (FYP) is where videos go viral. To get recommended, your content needs to be "FYP-safe":
              </p>

              <div className="seo-feature-list">
                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Shield size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Stay Family-Friendly</h4>
                    <p>TikTok prioritizes content that's safe for all ages. Even subtle adult themes can limit your reach.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Zap size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Avoid Trigger Words</h4>
                    <p>Certain words in captions or spoken audio can flag your video. Creators often use creative spellings ("unalive" instead of more direct terms).</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <TrendingUp size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Use Safe Sounds</h4>
                    <p>Some trending sounds get restricted. Stick to sounds from TikTok's commercial library if you want maximum reach.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Eye size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Check Visual Content</h4>
                    <p>TikTok's AI scans every frame. Avoid anything that could be flagged - even in the background of your videos.</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="stay-safe">
              <h2>How to Stay Safe on TikTok</h2>
              <p>
                TikTok's algorithm is notoriously unpredictable. Here's how to minimize your risk:
              </p>

              <ul>
                <li><strong>Know the guidelines</strong> - Read TikTok's Community Guidelines regularly as they update often</li>
                <li><strong>When in doubt, don't post</strong> - If you're unsure about content, it's probably not worth the risk</li>
                <li><strong>Watch your language</strong> - Use creative alternatives for sensitive words</li>
                <li><strong>Check before posting</strong> - Review your video for anything that could be flagged</li>
                <li><strong>Don't rely on one account</strong> - Many creators have backup accounts in case of bans</li>
                <li><strong>Appeal wrongful removals</strong> - TikTok's AI makes mistakes; you can appeal</li>
              </ul>

              <div className="seo-highlight-box">
                <p>
                  MyCleanStudio scans your video for visual content issues and checks against TikTok's guidelines before you post, helping you avoid shadowbans and removals.
                </p>
              </div>
            </section>

            {/* CTA */}
            <div className="seo-cta">
              <h2>Don't Risk Your TikTok Account</h2>
              <p>Scan your videos for visual issues and guideline violations before posting.</p>
              <Link to="/dashboard/new-check" className="seo-cta-button">
                Scan Your Video
                <ArrowRight size={18} />
              </Link>
            </div>
          </main>

          {/* Footer */}
          <footer className="seo-footer">
            <div className="seo-footer-links">
              <Link to="/guides/youtube-guidelines">YouTube Guidelines</Link>
              <Link to="/guides/youtube-demonetization">Demonetization Guide</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms of Service</Link>
            </div>
            <p>&copy; {new Date().getFullYear()} MyCleanStudio. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </>
  );
}

export default TikTokGuidelinesGuide;
